// Local-server registry for the akva-network-scanner page. Servers come from
// the Electron host (window.electronAPI.getAllSites()), the same source tide
// uses. The topbar machine picker selects one; its IP + the fixed akva port
// becomes the backend URL passed to the MFE. Shared between the picker
// (LocalServerPicker via useLocalServers) and the MFE registration
// (single-spa-config/apps.ts).
//
// This is an external store consumed through useSyncExternalStore: a single
// snapshot is the source of truth; `__OBSERVE_AKVA_BACKEND__` is a read-only
// projection of it the MFE bundle can read across the module boundary, so the
// two can never drift.

// Port the akva-network-scanner backend listens on, on the on-site machine.
// Only the port is fixed; the host (IP) comes from the selected local server.
const AKVA_SCANNER_PORT =
  import.meta.env.VITE_AKVA_NETWORK_SCANNER_PORT || "8765";

// Subset of the Electron SiteMap entry (full type: @observe-tech/utils SiteMap).
export type LocalServer = {
  machine_id: number;
  site_id: number;
  name: string;
  ip: string;
  port: string;
};

export type LocalServersSnapshot = {
  servers: LocalServer[];
  selectedMachineId: number | null;
};

export type RefreshResult = { ok: true } | { ok: false; error: unknown };

// The one window cast for the Electron bridge, defined once.
type ElectronLocalServerApi = {
  getAllSites?: () => Promise<Record<number, LocalServer>>;
};
function getElectronApi(): ElectronLocalServerApi | undefined {
  return (window as unknown as { electronAPI?: ElectronLocalServerApi })
    .electronAPI;
}

// The one window cast for the backend-URL projection, defined once.
function projectBackendGlobal(url: string | undefined): void {
  (
    window as unknown as { __OBSERVE_AKVA_BACKEND__?: string }
  ).__OBSERVE_AKVA_BACKEND__ = url;
}

let cache: Record<number, LocalServer> = {};
let selectedMachineId: number | null = null;
let snapshot: LocalServersSnapshot = { servers: [], selectedMachineId: null };
const subs = new Set<() => void>();

// Recompute the immutable snapshot, refresh the window projection and notify
// subscribers. The snapshot reference only changes here, so useSyncExternalStore
// stays stable between mutations.
function commit(): void {
  snapshot = { servers: Object.values(cache), selectedMachineId };
  projectBackendGlobal(akvaScannerBackendUrl());
  for (const cb of subs) cb();
}

export async function refreshLocalServers(): Promise<RefreshResult> {
  const electron = getElectronApi();
  if (!electron?.getAllSites) return { ok: true };
  try {
    cache = await electron.getAllSites();
    // Drop a stale selection if the machine is gone from the refreshed list.
    if (selectedMachineId != null && !cache[selectedMachineId]) {
      selectedMachineId = null;
    }
    commit();
    return { ok: true };
  } catch (error) {
    console.warn("[shell] getAllSites (local servers) failed", error);
    return { ok: false, error };
  }
}

export function getLocalServersSnapshot(): LocalServersSnapshot {
  return snapshot;
}

export function setSelectedMachineId(id: number | null): void {
  selectedMachineId = id;
  commit();
}

export function subscribeLocalServers(cb: () => void): () => void {
  subs.add(cb);
  return () => {
    subs.delete(cb);
  };
}

// Backend URL for the akva-network-scanner MFE: selected machine's IP + the
// fixed akva port. Undefined when nothing is selected or the IP is missing.
export function akvaScannerBackendUrl(): string | undefined {
  if (selectedMachineId == null) return undefined;
  const server = cache[selectedMachineId];
  if (!server?.ip) return undefined;
  return `https://${server.ip}:${AKVA_SCANNER_PORT}`;
}
